import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function Service() {
  const [loaded, setLoaded] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const roadmapRef = useRef(null);

  useEffect(() => {
    setLoaded(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.2 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const sectionStyles = {
    opacity: loaded ? 1 : 0,
    transform: loaded ? 'translateY(0)' : 'translateY(30px)',
    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
  };

  const cardStyles = {
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(74, 158, 255, 0.15)',
    borderRadius: '12px',
    padding: '2rem',
  };

  const roadmapData = [
    {
      year: 'Q3 2026',
      title: 'Layer-1 & RWA Expansion',
      description: 'Expanding our algorithmic framework to key Layer-1 and Real-World Asset crypto projects',
      items: ['HYPE', 'SUI', 'APT', 'HBAR', 'ONDO', 'PENDLE'],
      color: '#4a9eff',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      ),
    },
    {
      year: 'Q4 2026',
      title: 'AI Crypto Expansion',
      description: 'Further expansion of the algorithmic framework to include strategic AI-related crypto projects',
      items: ['TAO', 'RENDER', 'FET', 'NEAR'],
      color: '#4a9eff',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2a10 10 0 1 0 10 10" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 5v2M12 17v2M5 12h2M17 12h2" />
        </svg>
      ),
    },
    {
      year: 'Q1 2027',
      title: 'Crypto Market Integration',
      description: 'Combining asset strategies with broader market indexes',
      items: ['TOTAL', 'TOTAL2', 'TOTAL3', 'OTHERS', 'USDT.D', 'BTC.D', 'ETH/BTC', 'BTC/XAU', 'ETH/XAU'],
      color: '#4a9eff',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <polyline points="22,7 13.5,15.5 8.5,10.5 2,17" />
          <polyline points="16,7 22,7 22,13" />
        </svg>
      ),
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      padding: '120px 5% 80px',
    }}>
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
      }}>
        {/* Hero Section */}
        <div style={{
          ...sectionStyles,
          textAlign: 'center',
          marginBottom: '5rem',
        }}>
          <h1 style={{
            fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
            fontWeight: 700,
            color: '#ffffff',
            marginBottom: '1.5rem',
            lineHeight: 1.1,
          }}>
            Our Service
          </h1>
          <div style={{
            width: '80px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, #4a9eff 50%, transparent 100%)',
            margin: '0 auto 2rem',
          }} />
          <p style={{
            color: '#a8c5e6',
            fontSize: '1.15rem',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: 1.7,
          }}>
            Institutional-grade algorithmic trading research, delivered weekly
          </p>

          {/* Top Subscription CTA — same styling as the bottom Go to Subscription button */}
          <div style={{ marginTop: '2.5rem' }}>
            <Link
              to="/subscription"
              style={{
                display: 'inline-block',
                padding: '1.1rem 3rem',
                background: 'linear-gradient(135deg, #4a9eff 0%, #1a6ed8 100%)',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                boxShadow: '0 4px 20px rgba(74, 158, 255, 0.3)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={e => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 8px 30px rgba(74, 158, 255, 0.4)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(74, 158, 255, 0.3)';
              }}
            >
              Subscription
            </Link>
          </div>
        </div>

        {/* What We Offer Section */}
        <section
          id="offer"
          data-animate
          style={{
            ...cardStyles,
            marginBottom: '3rem',
            opacity: visibleSections['offer'] ? 1 : 0,
            transform: visibleSections['offer'] ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(74, 158, 255, 0.2) 0%, rgba(74, 158, 255, 0.05) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(74, 158, 255, 0.3)',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#ffffff',
              margin: 0,
            }}>
              What We Offer
            </h2>
          </div>

          <p style={{
            color: '#b8d4f1',
            fontSize: '1.05rem',
            lineHeight: 1.8,
            marginBottom: '2rem',
          }}>
            Parametrix Capital develops algorithmic trading strategies that deliver precise entry and exit signals 
            across a diversified portfolio of cryptocurrency assets. Each strategy incorporates statistically-validated 
            stop-loss levels tailored to the volatility profile of the specific asset.
          </p>

          <div style={{
            background: 'rgba(74, 158, 255, 0.05)',
            border: '1px solid rgba(74, 158, 255, 0.1)',
            borderRadius: '10px',
            padding: '1.5rem 2rem',
          }}>
            <h3 style={{
              color: '#4a9eff',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: '1.25rem',
            }}>
              Weekly Report Includes
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '0.75rem',
            }}>
              {[
                'New entry and exit signals from our algorithms',
                'Stop-loss values for each open position',
                'Status updates on current holdings',
                'Portfolio performance tracker for each asset',
                'Market context analysis and forward outlook',
              ].map((item, index) => (
                <li key={index} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  color: '#a8c5e6',
                  fontSize: '0.95rem',
                  lineHeight: 1.5,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Our Mission Section */}
        <section
          id="mission"
          data-animate
          style={{
            ...cardStyles,
            marginBottom: '4rem',
            borderLeft: '3px solid #4a9eff',
            opacity: visibleSections['mission'] ? 1 : 0,
            transform: visibleSections['mission'] ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(74, 158, 255, 0.2) 0%, rgba(74, 158, 255, 0.05) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(74, 158, 255, 0.3)',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#ffffff',
              margin: 0,
            }}>
              Our Mission
            </h2>
          </div>

          <p style={{
            color: '#b8d4f1',
            fontSize: '1.1rem',
            lineHeight: 1.9,
            marginBottom: '1.5rem',
          }}>
            Parametrix Capital was founded to cut through the noise of personal opinions and subjective analysis 
            that lack rigorous foundation. We provide investors with a disciplined, quantitative, and backtested 
            methodology designed for long-term success.
          </p>

          <p style={{
            color: '#a8c5e6',
            fontSize: '1.05rem',
            lineHeight: 1.8,
            fontStyle: 'italic',
            paddingLeft: '1.5rem',
            borderLeft: '2px solid rgba(74, 158, 255, 0.3)',
          }}>
            Our commitment to transparency means full traceability of every strategy's performance — 
            no black boxes, no hidden metrics, just data-driven conviction.
          </p>
        </section>

        {/* Roadmap Section */}
        <section
          id="roadmap"
          ref={roadmapRef}
          data-animate
          style={{
            marginBottom: '4rem',
            opacity: visibleSections['roadmap'] ? 1 : 0,
            transform: visibleSections['roadmap'] ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#ffffff',
              marginBottom: '0.75rem',
            }}>
              Roadmap
            </h2>
            <p style={{
              color: '#6b9bc9',
              fontSize: '1rem',
            }}>
              Our strategic expansion plan
            </p>
          </div>

          {/* Visual Timeline */}
          <div style={{
            position: 'relative',
            padding: '2rem 0',
          }}>
            {/* Timeline Line */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              background: 'linear-gradient(180deg, rgba(74, 158, 255, 0.4) 0%, #4a9eff 50%, rgba(74, 158, 255, 0.4) 100%)',
              transform: 'translateX(-50%)',
              zIndex: 0,
            }} className="timeline-line" />

            {/* Roadmap Items */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3rem',
              position: 'relative',
              zIndex: 1,
            }}>
              {roadmapData.map((item, index) => (
                <div
                  key={item.year}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '2rem',
                    flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                  }}
                  className="roadmap-item"
                >
                  {/* Content Card */}
                  <div style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: `1px solid ${item.color}30`,
                    borderRadius: '16px',
                    padding: '2rem',
                    position: 'relative',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = `0 20px 40px ${item.color}15`;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}05 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px solid ${item.color}40`,
                        color: item.color,
                      }}>
                        {item.icon}
                      </div>
                      <div>
                        <span style={{
                          display: 'block',
                          fontSize: '2rem',
                          fontWeight: 800,
                          color: item.color,
                          lineHeight: 1,
                        }}>
                          {item.year}
                        </span>
                        <span style={{
                          fontSize: '0.8rem',
                          color: '#6b9bc9',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                        }}>
                          Phase {index + 1}
                        </span>
                      </div>
                    </div>

                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#ffffff',
                      marginBottom: '0.5rem',
                    }}>
                      {item.title}
                    </h3>

                    <p style={{
                      color: '#a8c5e6',
                      fontSize: '0.95rem',
                      marginBottom: '1.25rem',
                      lineHeight: 1.6,
                    }}>
                      {item.description}
                    </p>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                    }}>
                      {item.items.map((tag, i) => (
                        <span key={i} style={{
                          padding: '0.35rem 0.75rem',
                          background: `${item.color}10`,
                          border: `1px solid ${item.color}25`,
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          color: item.color,
                          fontWeight: 500,
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Center Node */}
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: item.color,
                    border: '4px solid #0a1628',
                    boxShadow: `0 0 20px ${item.color}50`,
                    flexShrink: 0,
                    position: 'relative',
                    zIndex: 2,
                  }} className="timeline-node" />

                  {/* Spacer for alternating layout */}
                  <div style={{ flex: 1 }} className="roadmap-spacer" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section
          id="cta"
          data-animate
          style={{
            textAlign: 'center',
            padding: '3rem',
            background: 'linear-gradient(135deg, rgba(74, 158, 255, 0.1) 0%, rgba(74, 158, 255, 0.02) 100%)',
            border: '1px solid rgba(74, 158, 255, 0.2)',
            borderRadius: '16px',
            marginBottom: '3rem',
            opacity: visibleSections['cta'] ? 1 : 0,
            transform: visibleSections['cta'] ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease-out 0.6s, transform 0.8s ease-out 0.6s',
          }}
        >
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#ffffff',
            marginBottom: '1rem',
          }}>
            Ready to Get Started?
          </h2>
          <p style={{
            color: '#a8c5e6',
            fontSize: '1.05rem',
            marginBottom: '2rem',
            maxWidth: '500px',
            margin: '0 auto 2rem',
          }}>
            Subscribe to receive our weekly algorithmic trading signals and market analysis.
          </p>
          <Link
            to="/subscription"
            style={{
              display: 'inline-block',
              padding: '1.1rem 3rem',
              background: 'linear-gradient(135deg, #4a9eff 0%, #1a6ed8 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '1rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(74, 158, 255, 0.3)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 8px 30px rgba(74, 158, 255, 0.4)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 20px rgba(74, 158, 255, 0.3)';
            }}
          >
            Go to Subscription
          </Link>
        </section>

        {/* Disclaimer */}
        <aside style={{
          padding: '1.5rem 2rem',
          background: 'rgba(255, 255, 255, 0.01)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          borderLeft: '3px solid rgba(245, 166, 35, 0.5)',
        }}>
          <p style={{
            color: '#6b9bc9',
            fontSize: '0.8rem',
            lineHeight: 1.7,
            margin: 0,
            fontStyle: 'italic',
          }}>
            <strong style={{ color: '#a8c5e6', fontStyle: 'normal' }}>Disclaimer:</strong> Parametrix Capital does not provide financial advice or any regulated financial service. All content constitutes market research based on backtested historical data. Past performance does not guarantee future results. Consult a qualified financial advisor before acting on any trading signal with real capital.
          </p>
        </aside>
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 900px) {
          .roadmap-item {
            flex-direction: column !important;
            align-items: center !important;
          }
          .roadmap-spacer {
            display: none !important;
          }
          .timeline-line {
            left: 24px !important;
          }
          .timeline-node {
            position: absolute !important;
            left: 12px !important;
          }
          .roadmap-item > div:first-child {
            margin-left: 50px;
          }
        }
      `}</style>
    </div>
  );
}
